import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { notification } from "../../utils/Notifications";
import {
  getDeliveryGuides,
  getDeliveryGuidesBySuperadmin,
} from "../../api/guidesApi";
import { getZones } from "../../api/zonesApi";
import Title from "../../components/SharedComponents/Title";
import SearchBar from "../../components/SharedComponents/SearchBar";
import useLogout from "../../hooks/auth/authHook.js";
import styles from "./deliveryGuidesStyles.js";
import DeliveryGuidesTable from "../../components/DeliveryGuides/DeliveryGuidesTable/index.js";

const DeliveryGuides = () => {
  const [logout] = useLogout();
  const { extraInformation } = useSelector((state) => state.user);
  const isSuperAdmin = extraInformation?.id_role === 5;

  const [guides, setGuides] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null); // null = "Todas las zonas"
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedInitial = useRef(false);

  const fetchGuides = () => {
    setIsLoading(true);

    const fetchFunction = isSuperAdmin
      ? getDeliveryGuidesBySuperadmin
      : getDeliveryGuides;
    const fetchParams = isSuperAdmin
      ? selectedZone
        ? [Number(selectedZone)]
        : null
      : undefined;

    fetchFunction(fetchParams)
      .then((response) => {
        if (response.data.status_Code === 200) {
          setGuides(response.data.orders || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener las guías",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchZones = () => {
    getZones()
      .then((response) => {
        if (response.data.status_Code === 200) {
          setZones(response.data.ls_zones || []);
        } else if (response.data.status_Code === 401) {
          notification({
            text: "Sesión expirada. Por favor, inicia sesión nuevamente.",
            type: "error",
          });
          logout();
        } else {
          notification({
            text: "Error al obtener las zonas",
            type: "error",
          });
        }
      })
      .catch(() => {
        notification({
          text: "Error de conexión, revisa tu conexión a internet",
          type: "error",
        });
      });
  };

  const handleZoneChange = (event) => {
    const zoneValue = event.target.value;
    setSelectedZone(zoneValue === "" ? null : zoneValue);
  };

  useEffect(() => {
    fetchZones();
  }, []);

  useEffect(() => {
    if (extraInformation?.id_role !== undefined && !hasFetchedInitial.current) {
      hasFetchedInitial.current = true;
      fetchGuides();
    }
  }, [extraInformation?.id_role]);

  useEffect(() => {
    if (
      isSuperAdmin &&
      extraInformation?.id_role === 5 &&
      hasFetchedInitial.current
    ) {
      fetchGuides();
    }
  }, [selectedZone]);

  return (
    <Container style={styles.mainContainer}>
      <Title title="Guías por entregar" />
      <SearchBar setSearch={setSearch} />

      {isSuperAdmin && (
        <Container style={{ marginBottom: "16px", marginTop: "16px" }}>
          <FormControl variant="standard" style={{ minWidth: 200 }}>
            <InputLabel id="zone-filter-label">Filtrar por zona</InputLabel>
            <Select
              labelId="zone-filter-label"
              id="zone-filter"
              value={selectedZone || ""}
              onChange={handleZoneChange}
            >
              <MenuItem value="">
                <em>Todas las zonas</em>
              </MenuItem>
              {zones.map((zone) => {
                const zoneId =
                  typeof zone === "string"
                    ? zone
                    : zone.id_zone || zone.id || zone.zone_code;
                const zoneName =
                  typeof zone === "string"
                    ? zone
                    : zone.zone_name || zone.name || zone.zone_code || zone.id;
                return (
                  <MenuItem key={zoneId} value={zoneId}>
                    {zoneName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Container>
      )}

      <DeliveryGuidesTable
        guides={guides}
        search={search}
        fetchGuides={fetchGuides}
        isLoading={isLoading}
      />
    </Container>
  );
};

export default DeliveryGuides;
